import type { DurableEvent } from '@crowdset/contracts';

export type IngestResult = 'applied' | 'buffered' | 'stale' | 'duplicate' | 'held';

export interface SequencerOptions {
  apply(ev: DurableEvent): void;
  /** Called when a gap persists; the owner must fetch a snapshot and call onSnapshot. */
  requestSnapshot(reason: 'gap'): void;
  /** How long to wait for a missing event (cross-replica reordering) before refetching. */
  gapWaitMs?: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
}

/**
 * Orders durable room events against the snapshot sequence.
 *  - While a snapshot is in flight, live events are buffered (subscription and snapshot are not atomic).
 *  - An event is applied only when roomVersion === current + 1; older/duplicate ones are dropped.
 *  - Future events are held briefly; if the gap is not filled, a fresh snapshot is requested.
 */
export class EventSequencer {
  private version: number | null = null;
  private awaitingSnapshot = true;
  private buffer: DurableEvent[] = [];
  private held = new Map<number, DurableEvent>();
  private seen = new Set<string>();
  private seenOrder: string[] = [];
  private gapTimer: unknown = null;
  private readonly gapWaitMs: number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (t: unknown) => void;

  constructor(private readonly opts: SequencerOptions) {
    this.gapWaitMs = opts.gapWaitMs ?? 300;
    this.setTimer = opts.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = opts.clearTimer ?? ((t) => clearTimeout(t as ReturnType<typeof setTimeout>));
  }

  get currentVersion(): number | null {
    return this.version;
  }

  get isAwaitingSnapshot(): boolean {
    return this.awaitingSnapshot;
  }

  /** Mark that a snapshot request is in flight; live events are buffered until onSnapshot. */
  beginSnapshot(): void {
    this.awaitingSnapshot = true;
  }

  /** Snapshot failed: stop buffering against it (keep version, events will be retried by the next snapshot). */
  abortSnapshot(): void {
    this.awaitingSnapshot = false;
    const pending = this.buffer;
    this.buffer = [];
    if (this.version !== null) for (const ev of pending) this.ingest(ev);
  }

  onSnapshot(roomVersion: number): void {
    // A snapshot never moves us backwards if a newer one was already applied.
    if (this.version === null || roomVersion >= this.version) this.version = roomVersion;
    this.awaitingSnapshot = false;
    this.cancelGapTimer();
    const pending = [...this.buffer, ...this.held.values()].sort(
      (a, b) => a.roomVersion - b.roomVersion,
    );
    this.buffer = [];
    this.held.clear();
    for (const ev of pending) this.ingest(ev);
  }

  onEvent(ev: DurableEvent): IngestResult {
    if (this.seen.has(ev.eventId)) return 'duplicate';
    if (this.awaitingSnapshot || this.version === null) {
      this.buffer.push(ev);
      return 'buffered';
    }
    return this.ingest(ev);
  }

  reset(): void {
    this.version = null;
    this.awaitingSnapshot = true;
    this.buffer = [];
    this.held.clear();
    this.cancelGapTimer();
  }

  private ingest(ev: DurableEvent): IngestResult {
    if (this.seen.has(ev.eventId)) return 'duplicate';
    const v = this.version!;
    if (ev.roomVersion <= v) {
      this.markSeen(ev.eventId);
      return 'stale';
    }
    if (ev.roomVersion === v + 1) {
      this.applyOne(ev);
      this.drainHeld();
      return 'applied';
    }
    this.held.set(ev.roomVersion, ev);
    this.scheduleGapCheck();
    return 'held';
  }

  private applyOne(ev: DurableEvent) {
    this.version = ev.roomVersion;
    this.markSeen(ev.eventId);
    this.opts.apply(ev);
  }

  private drainHeld() {
    for (;;) {
      const next = this.held.get(this.version! + 1);
      if (!next) break;
      this.held.delete(next.roomVersion);
      if (!this.seen.has(next.eventId)) this.applyOne(next);
      else this.version = next.roomVersion;
    }
    for (const k of [...this.held.keys()]) if (k <= this.version!) this.held.delete(k);
    if (this.held.size === 0) this.cancelGapTimer();
  }

  private scheduleGapCheck() {
    if (this.gapTimer !== null) return;
    this.gapTimer = this.setTimer(() => {
      this.gapTimer = null;
      if (this.held.size > 0 && !this.awaitingSnapshot) {
        this.awaitingSnapshot = true;
        this.opts.requestSnapshot('gap');
      }
    }, this.gapWaitMs);
  }

  private cancelGapTimer() {
    if (this.gapTimer !== null) {
      this.clearTimer(this.gapTimer);
      this.gapTimer = null;
    }
  }

  private markSeen(id: string) {
    if (this.seen.has(id)) return;
    this.seen.add(id);
    this.seenOrder.push(id);
    if (this.seenOrder.length > 500) this.seen.delete(this.seenOrder.shift()!);
  }
}
